import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import ForceLogoutModal from '../components/ForceLogoutModal';

const SocketContext = createContext();

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};

// ✅ Create a global socket instance (prevents duplicate connections)
let globalSocket = null;

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [logoutModal, setLogoutModal] = useState({ isOpen: false, message: '' });
    // ✅ Flag: once force-logout is triggered, prevent it from firing again
    const logoutTriggered = useRef(false);

    const triggerForceLogout = (message) => {
        // Do not trigger if already on auth or landing pages
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/auth') || currentPath === '/landing') {
            return;
        }

        // Guard: only show once per session
        if (logoutTriggered.current) return;
        logoutTriggered.current = true;
        setLogoutModal({ isOpen: true, message });
    };

    const handleForceLogoutConfirm = async () => {
        // ✅ Disconnect socket immediately so no more events fire
        if (globalSocket) {
            globalSocket.removeAllListeners();
            globalSocket.disconnect();
            globalSocket = null;
        }

        // ✅ Clear local credentials immediately
        localStorage.removeItem('ff_arena_token');
        localStorage.removeItem('ff_arena_user_session');
        delete axios.defaults.headers.common['Authorization'];

        try {
            // ✅ Force clear cookies via server
            await axios.post(`${backendUrl}/api/clear-session`);
        } catch (err) {
        }

        setLogoutModal({ isOpen: false, message: '' });
        window.location.replace('/auth');
    };

    useEffect(() => {
        // Prevent multiple connections
        if (globalSocket) {
            setSocket(globalSocket);
            return;
        }

        // ✅ Your backend URL
        const socketUrl = `${backendUrl}`;

        // Initialize socket once
        const newSocket = io(socketUrl, {
            withCredentials: true,
            transports: ['websocket', 'polling'],
            autoConnect: true,
        });

        globalSocket = newSocket; // save globally
        setSocket(newSocket);

        // ---- CONNECTION STATUS ----
        newSocket.on('connect', () => {
            setIsConnected(true);

            // ✅ Auto-register user from saved session (zero network overhead, avoids 401 loop on auth page)
            const currentPath = window.location.pathname;
            if (currentPath.startsWith('/auth') || currentPath === '/landing') {
                return;
            }

            try {
                const savedUserStr = localStorage.getItem('ff_arena_user_session');
                if (savedUserStr) {
                    const user = JSON.parse(savedUserStr);
                    const userId = user?.id || user?._id;
                    if (userId) {
                        newSocket.emit('register-user', userId);
                    }
                }
            } catch (error) {
                // Ignore parse errors
            }
        });

        newSocket.on('disconnect', (reason) => {
            setIsConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            setIsConnected(false);
        });

        // ---- FORCE LOGOUT (SINGLE DEVICE) ----
        newSocket.on('force-logout', (data) => {
            localStorage.removeItem('ff_arena_token');
            localStorage.removeItem('ff_arena_user_session');
            delete axios.defaults.headers.common['Authorization'];
            triggerForceLogout(data.message || 'Your session has expired. You have been logged in on another device.');
        });

        // ---- AXIOS INTERCEPTOR FOR SESSION EXPIRED ----
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                const isSessionExpired = error.response?.status === 401 && error.response?.data?.reason === 'session_expired';

                if (isSessionExpired) {
                    localStorage.removeItem('ff_arena_token');
                    localStorage.removeItem('ff_arena_user_session');
                    delete axios.defaults.headers.common['Authorization'];
                    triggerForceLogout(error.response.data.message || 'Your session has expired. You have been logged in on another device.');
                }
                return Promise.reject(error);
            }
        );

        // Cleanup only on full app unload
        return () => {
            axios.interceptors.response.eject(interceptor);
            if (globalSocket) {

            }
        };
    }, []);

    const value = { socket, isConnected };

    return (
        <SocketContext.Provider value={value}>
            {children}
            <ForceLogoutModal
                isOpen={logoutModal.isOpen}
                message={logoutModal.message}
                onConfirm={handleForceLogoutConfirm}
            />
        </SocketContext.Provider>
    );
};
